import React, { Fragment, useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import ModuleViewSelector from "./ModuleViewSelector";

/**
 * ModuleLayout - Reusable layout for all modules
 * Supports both flat views and categorized tree structure
 *
 * @param {Object} props
 * @param {string} props.moduleTitle - Title of the module (e.g., "Admin Management")
 * @param {Array} props.views - Array of view objects OR category objects with subViews
 * @param {string} props.defaultView - ID of the default view to display
 * @param {Function} props.renderContent - Function that receives (activeView, viewData) and returns content to display
 * @param {Object} props.breadcrumbItems - Optional breadcrumb configuration
 * @param {boolean} props.hideViewSelector - When true, hide the left sidebar view selector
 * @param {string} props.initialView - Controlled view from outside (URL params)
 */
const ModuleLayout = ({
  moduleTitle,
  views = [],
  defaultView,
  renderContent,
  breadcrumbItems = [],
  hideViewSelector = false,
  initialView = null,
}) => {
  const [activeView, setActiveView] = useState(null);
  const [accessibleViews, setAccessibleViews] = useState([]);
  const [flattenedViews, setFlattenedViews] = useState([]);

  // Get current user type from localStorage
  const getUserType = () => {
    try {
      const userData = localStorage.getItem("user_data");
      if (userData) {
        const parsedData = JSON.parse(userData);
        return parsedData.userType;
      }
      return localStorage.getItem("user_type") || null;
    } catch (error) {
      return null;
    }
  };

  // Check if views are in categorized structure
  const isCategorizedStructure = views.length > 0 && views[0].category !== undefined;

  // Filter views and resolve the active view.
  // NOTE: activeView is intentionally NOT in the dependency array — reading it
  // via the functional setter avoids a loop where setActiveView re-triggers
  // this effect endlessly.
  useEffect(() => {
    const currentUserType = getUserType();

    let filtered;
    let flattened = [];

    if (isCategorizedStructure) {
      filtered = views.map((category) => {
        const accessibleSubViews = (category.subViews || []).filter((subView) => {
          if (!subView.whoCanAccess || subView.whoCanAccess.length === 0) return true;
          return subView.whoCanAccess.includes(currentUserType);
        });
        flattened = [...flattened, ...accessibleSubViews];
        return { ...category, subViews: accessibleSubViews };
      }).filter((category) => category.subViews.length > 0);
    } else {
      filtered = views.filter((view) => {
        if (!view.whoCanAccess || view.whoCanAccess.length === 0) return true;
        return view.whoCanAccess.includes(currentUserType);
      });
      flattened = filtered;
    }

    setAccessibleViews(filtered);
    setFlattenedViews(flattened);

    if (flattened.length > 0) {
      // Use functional setter to read current activeView without adding it to deps.
      // Priority: explicit initialView (URL-driven) > keep current if accessible > defaultView > first
      setActiveView(prev => {
        if (initialView && flattened.find(v => v.id === initialView)) return initialView;
        if (prev && flattened.find(v => v.id === prev)) return prev;
        if (defaultView && flattened.find(v => v.id === defaultView)) return defaultView;
        return flattened[0].id;
      });
    } else {
      setActiveView(null);
    }
  }, [views, defaultView, isCategorizedStructure, initialView]);

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
  };

  // Find active view data from flattened views
  const activeViewData = flattenedViews.find((view) => view.id === activeView);

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={moduleTitle}
        parent="Home"
        title={moduleTitle}
        {...breadcrumbItems}
      />
      <Container fluid={true}>
        <Row>
          {/* Left Sidebar - View Selector */}
          {!hideViewSelector && (
            <Col xl="2" lg="3" md="3" className="box-col-4">
              <Card>
                <CardBody>
                  <ModuleViewSelector
                    views={accessibleViews}
                    activeView={activeView}
                    onViewChange={handleViewChange}
                  />
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Main Content Area */}
          <Col xl={hideViewSelector ? "12" : "10"} lg={hideViewSelector ? "12" : "9"} md={hideViewSelector ? "12" : "9"} className={hideViewSelector ? "" : "box-col-8"}>
            <Card>
              <CardBody>
                {flattenedViews.length === 0 ? (
                  <div className="text-center py-5">
                    <h5 className="text-muted">No accessible views</h5>
                    <p className="text-muted">You don't have permission to access any views in this module.</p>
                  </div>
                ) : (
                  renderContent && renderContent(activeView, activeViewData)
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ModuleLayout;
